/**
 * Ranking Service
 *
 * The two leaderboard scopes behind `/standing`, both read-only:
 * - `GET /api/ranking/friends` → {@link FriendsRankingBoard}
 * - `GET /api/ranking/global`  → {@link GlobalRankingBoard}
 *
 * Deliberately **not** `CrudService.getAll()`, which unwraps the response to a
 * bare array and discards everything else (`crud.service.ts:288-305`). Both
 * boards carry `viewer` alongside the rows, and the global one carries
 * `distribution` and `meta` as well — all of which that unwrap would throw
 * away. It is the same trap the friends list hit with its `metadata` envelope.
 *
 * `CrudService` is still the base for its cache and configuration; only the
 * reads are bespoke.
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';

import type {
  UserRank,
  RankingScope,
  FriendsRankingBoard,
  GlobalRankingBoard,
} from '../types/domain/ranking.types';

/**
 * Which week to read.
 *
 * `date` is any `YYYY-MM-DD` inside the week you want; the server resolves it
 * to that week's Monday. Omit it for the default — the last *completed* week,
 * which is the only window the board is ever published for.
 *
 * Anything older than that default is a Premium feature: the server answers
 * `402` through the `view-historical-rankings` gate, which the global paywall
 * presenter turns into a page-level refusal.
 */
export interface RankingQuery {
  /** `YYYY-MM-DD`. Omit for the last completed week. */
  date?: string;
}

/**
 * RankingService provides strongly-typed access to both leaderboard scopes.
 */
export class RankingService extends CrudService<UserRank> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/ranking',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // CORE RANKING OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Friends board: every row, plus where the viewer stands among them.
   *
   * GET /api/ranking/friends
   */
  public async getFriendsRanking(
    query?: RankingQuery
  ): Promise<FriendsRankingBoard> {
    return this.getBoard<FriendsRankingBoard>('friends', query);
  }

  /**
   * Global board: the top of the field, the viewer's neighbourhood, the shape
   * of the whole distribution, and the size of the gap between them.
   *
   * GET /api/ranking/global
   */
  public async getGlobalRanking(
    query?: RankingQuery
  ): Promise<GlobalRankingBoard> {
    return this.getBoard<GlobalRankingBoard>('global', query);
  }

  // ---------------------------------------------------------------------------
  // INTERNALS
  // ---------------------------------------------------------------------------

  private async getBoard<TBoard extends FriendsRankingBoard>(
    scope: RankingScope,
    query?: RankingQuery
  ): Promise<TBoard> {
    const cacheKey = this.boardCacheKey(scope, query);
    const cached = this.cache.get<TBoard>(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const response = await apiClient.get<TBoard>(`/ranking/${scope}`, {
        params: query?.date ? { date: query.date } : undefined,
      });

      // Only a published board is cached. Once the Monday pass has scored the
      // week the board is final, so five minutes of reuse costs nothing; the
      // unscored board is the one answer certain to change, and cached it
      // outlived the pass that replaced it — a screen reopened after the job
      // kept saying "still being worked out", each scope until its own entry
      // expired.
      if (response.data.viewer.reason !== 'not_yet_scored') {
        this.cache.set(cacheKey, response.data, this.cacheTTL);
      }

      return response.data;
    } catch (error) {
      console.error(`Failed to fetch ${scope} ranking:`, error);
      rethrowTyped(
        error,
        `Unable to fetch ${scope} ranking. Please try again.`
      );
    }
  }

  /**
   * The week is part of the key, not just the scope.
   *
   * Keyed on scope alone, stepping back through the week picker would be served
   * the previous week's board from cache for every date — the picker's entire
   * job, silently defeated for the length of the TTL.
   */
  private boardCacheKey(scope: RankingScope, query?: RankingQuery): string {
    return this.generateCacheKey(`ranking-${scope}`, query?.date ?? 'default');
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default ranking service instance.
 */
export const rankingService = new RankingService();
